// QuickFilters - Horizontal scrollable filter chips for fast discovery

import React, { useRef, useState, useEffect } from 'react';
import {
  Coffee,
  BookOpen,
  Music,
  Users,
  Dog,
  Accessibility,
  Clock,
  MapPin,
  Sparkles,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import type { MainCategory, AnyCategory } from '../../types';
import { MAIN_CATEGORIES } from '../../types';

export type QuickFilterType =
  | 'open_now'
  | 'nearby'
  | 'cafe'
  | 'quiet'
  | 'dogs'
  | 'accessible'
  | 'live_music'
  | 'coworking'
  | 'outdoor';

interface QuickFilter {
  id: QuickFilterType;
  label: string;
  icon: React.ReactNode;
  categories?: AnyCategory[];  // Now supports both GoodLife and GoodWork categories
  moods?: string[];
  activities?: string[];
  community?: string[];
}

// Extend QuickFilter with categories for type safety
interface QuickFilterWithCategories extends QuickFilter {
  categories?: AnyCategory[];
}

const QUICK_FILTERS: QuickFilter[] = [
  {
    id: 'open_now',
    label: 'Jetzt offen',
    icon: <Clock className="w-4 h-4" />,
  },
  {
    id: 'nearby',
    label: 'In der Naehe',
    icon: <MapPin className="w-4 h-4" />,
  },
  {
    id: 'cafe',
    label: 'Wohnzimmer',
    icon: <Coffee className="w-4 h-4" />,
    categories: ['public_living_room'],
  },
  {
    id: 'quiet',
    label: 'Ruhig',
    icon: <BookOpen className="w-4 h-4" />,
    moods: ['ruhig', 'entspannt'],
  },
  {
    id: 'coworking',
    label: 'Arbeiten',
    icon: <Sparkles className="w-4 h-4" />,
    activities: ['Lesen', 'Arbeiten'],
  },
  {
    id: 'live_music',
    label: 'Events',
    icon: <Music className="w-4 h-4" />,
    activities: ['Events', 'Veranstaltungen'],
  },
  {
    id: 'dogs',
    label: 'Haustiere',
    icon: <Dog className="w-4 h-4" />,
    community: ['Haustiere willkommen'],
  },
  {
    id: 'accessible',
    label: 'Barrierefrei',
    icon: <Accessibility className="w-4 h-4" />,
    community: ['Barrierefrei'],
  },
  {
    id: 'outdoor',
    label: 'Draussen',
    icon: <Users className="w-4 h-4" />,
    moods: ['offen'],
    categories: ['natur_outdoor'],
  },
];

interface QuickFiltersProps {
  activeFilters: QuickFilterType[];
  onFilterToggle: (filterId: QuickFilterType) => void;
  onClearAll: () => void;
  isOpenNowAvailable?: boolean;
  isNearbyAvailable?: boolean;
}

export function QuickFilters({
  activeFilters,
  onFilterToggle,
  onClearAll,
  isOpenNowAvailable = true,
  isNearbyAvailable = false,
}: QuickFiltersProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position for arrow visibility
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(checkScroll, 300);
    }
  };

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="relative mb-6">
      {/* Left scroll arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-50 transition"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
      )}

      {/* Filter chips container */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-2 overflow-x-auto scrollbar-hide px-1 py-1 -mx-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Clear all button (shown when filters active) */}
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-200 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition whitespace-nowrap"
          >
            <X className="w-3.5 h-3.5" />
            Zuruecksetzen
          </button>
        )}

        {/* Filter chips */}
        {QUICK_FILTERS.map((filter) => {
          const isActive = activeFilters.includes(filter.id);
          const isDisabled =
            (filter.id === 'open_now' && !isOpenNowAvailable) ||
            (filter.id === 'nearby' && !isNearbyAvailable);

          return (
            <button
              key={filter.id}
              onClick={() => !isDisabled && onFilterToggle(filter.id)}
              disabled={isDisabled}
              className={`
                flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium transition whitespace-nowrap
                ${isActive
                  ? 'border-indigo-500 bg-indigo-500 text-white'
                  : isDisabled
                  ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {filter.icon}
              {filter.label}
              {filter.id === 'nearby' && !isNearbyAvailable && (
                <span className="text-xs text-gray-400">(GPS)</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Right scroll arrow */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-50 transition"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      )}
    </div>
  );
}

// Helper function to get filter criteria
export function getFilterCriteria(filterId: QuickFilterType): QuickFilter | undefined {
  return QUICK_FILTERS.find(f => f.id === filterId);
}

export default QuickFilters;
